import { GetServerSideProps } from "next";
import Breadcrumbs from "@/utils/Breadcrumbs";
import SocialCounter from "@/pages/stats/SocialCounter";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState, useRef } from "react";
import AgeGenderDistributionChart from "../../../pages/stats/AgeGenderDistributionChart";
import LinkedInUserDistributionChart from "../../../pages/stats/LinkedInUserDistributionChart";
import Head from "next/head";
import BreadcrumbSchema from "@/schema/BreadcrumbSchema";
import { FAQSchema } from "@/schema/FAQSchema";
import { toPng } from "html-to-image";

// Fetch data server-side using getServerSideProps
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug, social } = context.query;
  
  // Check for the specific patterns in slug and social
  const isSlugInvalid = Array.isArray(slug) ? slug.includes('[slug]') : slug === '[slug]';
  const isSocialInvalid = Array.isArray(social) ? social.some(s => s.startsWith('[social]-')) : social && social.startsWith('[social]-');

  // Redirect if conditions are met
  if (isSlugInvalid || isSocialInvalid) {
    return {
      redirect: {
        destination: '/stats',
        permanent: false, // Set to true for a permanent redirect (301)
      },
    };
  }

  const API_URL = `${process.env.NEXT_PUBLIC_MAIN_API_URL}/b2b/get-all-country`;

  // Ensure that social is a string (handle array case as well)
  const socialStr = Array.isArray(social) ? social[0] : social;

  // Example: "facebook-users-in-august-2024" => ["facebook-users", "august", "2024"]
  const socialParts = socialStr?.split("-in-") || [];
  const platformName = socialParts[0]?.split("-")?.[0]; // Get the first part before '-in-'

  // Format the platform name (e.g., "facebook" -> "Facebook")
  const formattedPlatformName = platformName
    ? toCamelCaseWithSpaces(platformName)
    : "";

  // Extract month and year
  const [month, year] = socialParts[1]?.split("-") || [];
  const formattedCountryName = toCamelCaseWithSpaces(
    typeof slug === "string" ? slug : ""
  );
  const formattedMonthString = `${year}-${monthMap[month] || "00"}`;

  let statsData = null;
  let error = null;

  try {
    // Fetch country data
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching countries: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      const selectedCountry = data.data.c_l.find(
        (country: any) => country.n === formattedCountryName
      );
      if (selectedCountry) {
        // Fetch platform stats for the selected country
        const statsResponse = await fetch(
          `${process.env.SOCIAL_STATS_API_URL}/api?type=platform&platform=${platformName}&countryId=${selectedCountry.i}&month=${formattedMonthString}`
        );

        if (!statsResponse.ok) {
          throw new Error(`Error fetching stats: ${statsResponse.status}`);
        }

        statsData = await statsResponse.json();

        // Check if the platform is LinkedIn and structure the data accordingly
        if (platformName.toLowerCase() === "linkedin") {
          statsData = {
            stats: {
              total: statsData.stats.total,
              population_18_24: statsData.stats.population_18_24,
              percentage_18_24: statsData.stats.percentage_18_24,
              population_25_34: statsData.stats.population_25_34,
              percentage_25_34: statsData.stats.percentage_25_34,
              population_35_54: statsData.stats.population_35_54,
              percentage_35_54: statsData.stats.percentage_35_54,
              population_55_plus: statsData.stats.population_55_plus,
              percentage_55_plus: statsData.stats.percentage_55_plus,
              description: statsData.stats.description,
            },
          };
        } else {
          // For other platforms, ensure the expected structure
          statsData = {
            stats: {
              total: statsData.stats.total,
              womenTotalPercentage: statsData.stats.womenTotalPercentage,
              menTotalPercentage: statsData.stats.menTotalPercentage,
              women_13_17: statsData.stats.women_13_17,
              women_13_17_percent: statsData.stats.women_13_17_percent,
              men_13_17: statsData.stats.men_13_17,
              men_13_17_percent: statsData.stats.men_13_17_percent,
              women_18_24: statsData.stats.women_18_24,
              women_18_24_percent: statsData.stats.women_18_24_percent,
              men_18_24: statsData.stats.men_18_24,
              men_18_24_percent: statsData.stats.men_18_24_percent,
              women_25_34: statsData.stats.women_25_34,
              women_25_34_percent: statsData.stats.women_25_34_percent,
              men_25_34: statsData.stats.men_25_34,
              men_25_34_percent: statsData.stats.men_25_34_percent,
              women_35_44: statsData.stats.women_35_44,
              women_35_44_percent: statsData.stats.women_35_44_percent,
              men_35_44: statsData.stats.men_35_44,
              men_35_44_percent: statsData.stats.men_35_44_percent,
              women_45_54: statsData.stats.women_45_54,
              women_45_54_percent: statsData.stats.women_45_54_percent,
              men_45_54: statsData.stats.men_45_54,
              men_45_54_percent: statsData.stats.men_45_54_percent,
              women_55_64: statsData.stats.women_55_64,
              women_55_64_percent: statsData.stats.women_55_64_percent,
              men_55_64: statsData.stats.men_55_64,
              men_55_64_percent: statsData.stats.men_55_64_percent,
              women_65_plus: statsData.stats.women_65_plus,
              women_65_plus_percent: statsData.stats.women_65_plus_percent,
              men_65_plus: statsData.stats.men_65_plus,
              men_65_plus_percent: statsData.stats.men_65_plus_percent,
              age_13_17_total_percent: statsData.stats.age_13_17_total_percent,
              age_18_24_total_percent: statsData.stats.age_18_24_total_percent,
              age_25_34_total_percent: statsData.stats.age_25_34_total_percent,
              age_35_44_total_percent: statsData.stats.age_35_44_total_percent,
              age_45_54_total_percent: statsData.stats.age_45_54_total_percent,
              age_55_64_total_percent: statsData.stats.age_55_64_total_percent,
              age_65_plus_total_percent:
                statsData.stats.age_65_plus_total_percent,
              description: statsData.stats.description,
            },
          };
        }
      } else {
        error = "Country not found";
      }
    } else {
      error = "Failed to fetch country data";
    }
  } catch (err: any) {
    error = err.message;
  }

   // Redirect to the specific stats URL if platform is LinkedIn and year is 2018
   if (platformName.toLowerCase() === "linkedin" && year === "2018") {
    return {
      redirect: {
        destination: `/stats/${formattedCountryName}/social-media-users-in-${month}-${year}`,
        permanent: false,
      },
    };
  }

  return {
    props: {
      platformName: formattedPlatformName,
      formattedCountryName,
      month: toCamelCaseWithSpaces(month),
      year,
      statsData,
      error,
    },
  };
};

// Function to convert strings to Camel Case with spaces
const toCamelCaseWithSpaces = (str: string) => {
  if (!str) return ""; // Handle undefined or empty string
  return str
    .split("-") // Split by hyphens
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize the first letter of each word
    )
    .join(" "); // Join words with a space
};

const monthMap: { [key: string]: string } = {
  january: "01",
  february: "02",
  march: "03",
  april: "04",
  may: "05",
  june: "06",
  july: "07",
  august: "08",
  september: "09",
  october: "10",
  november: "11",
  december: "12",
};

interface StatsDetailProps {
  platformName: string;
  formattedCountryName: string;
  month: string;
  year: string;
  statsData: {
    stats: {
      total: number;
      // LinkedIn-specific stats
      population_18_24?: number;
      percentage_18_24?: number;
      population_25_34?: number;
      percentage_25_34?: number;
      population_35_54?: number;
      percentage_35_54?: number;
      population_55_plus?: number;
      percentage_55_plus?: number;
      // General stats for other platforms
      womenTotalPercentage?: number;
      menTotalPercentage?: number;
      women_13_17?: number;
      women_13_17_percent?: number;
      men_13_17?: number;
      men_13_17_percent?: number;
      women_18_24?: number;
      women_18_24_percent?: number;
      men_18_24?: number;
      men_18_24_percent?: number;
      women_25_34?: number;
      women_25_34_percent?: number;
      men_25_34?: number;
      men_25_34_percent?: number;
      women_35_44?: number;
      women_35_44_percent?: number;
      men_35_44?: number;
      men_35_44_percent?: number;
      women_45_54?: number;
      women_45_54_percent?: number;
      men_45_54?: number;
      men_45_54_percent?: number;
      women_55_64?: number;
      women_55_64_percent?: number;
      men_55_64?: number;
      men_55_64_percent?: number;
      women_65_plus?: number;
      women_65_plus_percent?: number;
      men_65_plus?: number;
      men_65_plus_percent?: number;
      age_13_17_total_percent?: number;
      age_18_24_total_percent?: number;
      age_25_34_total_percent?: number;
      age_35_44_total_percent?: number;
      age_45_54_total_percent?: number;
      age_55_64_total_percent?: number;
      age_65_plus_total_percent?: number;
      description?: string;
    };
  } | null;
  error: string | null;
}

const Stats: React.FC<StatsDetailProps> = ({ statsData, error }) => {
  const router = useRouter();

  // Destructure stats from statsData
  const { stats } = statsData || {};

  // Destructure properties from stats with optional chaining
  const {
    total,
    // LinkedIn-specific stats
    population_18_24,
    percentage_18_24,
    population_25_34,
    percentage_25_34,
    population_35_54,
    percentage_35_54,
    population_55_plus,
    percentage_55_plus,

    // Other platform-specific stats
    womenTotalPercentage,
    menTotalPercentage,
    women_13_17,
    women_13_17_percent,
    men_13_17,
    men_13_17_percent,
    women_18_24,
    women_18_24_percent,
    men_18_24,
    men_18_24_percent,
    women_25_34,
    women_25_34_percent,
    men_25_34,
    men_25_34_percent,
    women_35_44,
    women_35_44_percent,
    men_35_44,
    men_35_44_percent,
    women_45_54,
    women_45_54_percent,
    men_45_54,
    men_45_54_percent,
    women_55_64,
    women_55_64_percent,
    men_55_64,
    men_55_64_percent,
    women_65_plus,
    women_65_plus_percent,
    men_65_plus,
    men_65_plus_percent,
    age_13_17_total_percent,
    age_18_24_total_percent,
    age_25_34_total_percent,
    age_35_44_total_percent,
    age_45_54_total_percent,
    age_55_64_total_percent,
    age_65_plus_total_percent,
    description,
  } = stats || {}; // Default to an empty object to avoid destructuring errors

  // Now you can use these variables safely in your component

  // Function to convert strings to Camel Case with spaces
  const toCamelCaseWithSpaces = (str: string) => {
    if (!str) return ""; // Handle undefined or empty string
    return str
      .split("-") // Split by hyphens
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize the first letter of each word
      )
      .join(" "); // Join words with a space
  };

  const { slug, social } = router.query;

  // Ensure that social is a string (handle array case as well)
  const socialStr = Array.isArray(social) ? social[0] : social;

  // Example: "facebook-users-in-august-2024" => ["facebook-users", "august", "2024"]
  const socialParts = socialStr?.split("-in-") || [];
  const platformName = socialParts[0]?.split("-")?.[0]; // Get the first part before '-in-'

  // Format the platform name (e.g., "facebook" -> "Facebook")
  const formattedPlatformName = platformName
    ? toCamelCaseWithSpaces(platformName)
    : "";

  // Extract month and year
  const [month, year] = socialParts[1]?.split("-") || [];
  const countryName = typeof slug === "string" ? slug : "";
  const formattedCountryName = toCamelCaseWithSpaces(countryName);

  console.log(formattedCountryName)

  const formattedMonthName = toCamelCaseWithSpaces(month);

  const monthMap: { [key: string]: string } = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };

  const formattedMonthString = `${year}-${monthMap[month] || "00"}`;

  // console.log(formattedMonthString);

  const generateYearlyMonthList = (
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number
  ): Record<number, string[]> => {
    const result: Record<number, string[]> = {};
    let currentYear = startYear;
    let currentMonth = startMonth;

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      if (!result[currentYear]) {
        result[currentYear] = [];
      }

      result[currentYear].push(
        `${currentYear}/${String(currentMonth).padStart(2, "0")}`
      );

      currentMonth += 1;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear += 1;
      }
    }

    return result;
  };

  const location = [
    { text: "Home", url: "/" },
    { text: "Stats", url: "/stats" },
    {
      text: formattedCountryName || "Unknown Country",
      url: slug
        ? `/stats/social-media-users-in-${slug}`
        : "/stats/unknown-country",
    },
    {
      text: formattedMonthName || "Unknown Month",
      url:
        formattedCountryName && month && year
          ? `/stats/${slug}/social-media-users-in-${month}-${year}`
          : "/stats/unknown-date",
    },
    {
      text: `${formattedPlatformName} Users`,
    },
  ];

  const breadcrumbItems = [
    { name: "Home", url: "https://www.bizdataset.com/" },
    { name: "Stats", url: "https://www.bizdataset.com/stats" },
    {
      name: formattedCountryName || "Unknown Country",
      url: slug
        ? `https://www.bizdataset.com/stats/social-media-users-in-${slug}`
        : "/stats/unknown-country",
    },
    {
      name: formattedMonthName || "Unknown Month",
      url:
        formattedCountryName && month && year
          ? `https://www.bizdataset.com/stats/${slug}/social-media-users-in-${month}-${year}`
          : "/stats/unknown-date",
    },
    {
      name: `${formattedPlatformName} Users`,
      url:
        formattedCountryName && month && year
          ? `https://www.bizdataset.com/stats/${slug}/${platformName}-users-in-${month}-${year}`
          : "/stats/unknown-date",
    },
  ];

  const formatNumberWithSpaces = (number: number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " "); // Format with spaces
  };

  const messages = [
    <>
      <div className="text-center">{formatNumberWithSpaces(total || 0)}</div>
      <span className="gradient-new-text">
        {formattedPlatformName} users
      </span>{" "}
      in {formattedCountryName || "Country"}
    </>,
  ];

  const totalRecords = total ?? 0;

  // Define an object to map platform names to their respective icons
  const platformIcons: Record<string, JSX.Element> = {
    facebook: (
      <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 320 512"
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"></path>
      </svg>
    ),
    messenger: (
      <svg
        key="icon"
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 512 512"
        height="28px"
        width="28px"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23A19.92 19.92 0 0 0 122 502.31c52.91-23.3 53.59-25.14 62.56-22.7C337.85 521.8 504 423.7 504 248.57 504 110.34 396.59 8 256.55 8zm149.24 185.13l-73 115.57a37.37 37.37 0 0 1-53.91 9.93l-58.08-43.47a15 15 0 0 0-18 0l-78.37 59.44c-10.46 7.93-24.16-4.6-17.11-15.67l73-115.57a37.36 37.36 0 0 1 53.91-9.93l58.06 43.46a15 15 0 0 0 18 0l78.41-59.38c10.44-7.98 24.14 4.54 17.09 15.62z"></path>
      </svg>
    ),
    instagram: (
      <svg
        key="icon"
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height="30px"
        width="30px"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
      </svg>
    ),
    linkedin: (
      <svg
        key="icon"
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height="30px"
        width="30px"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z"></path>
      </svg>
    ),
    // Add more platform icons as needed
  };

  // Select the icon based on the formatted platform name
  const selectedIcon = platformName
    ? platformIcons[platformName.toLowerCase()]
    : null;

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];



// Function to get the year/month parameters based on the platform
const getYearMonthParameters = (platformName: string): [number, number, number, number] => {
  if (['facebook', 'instagram', 'messenger'].includes(platformName)) {
    return [2018, 9, 2024, 8]; // Start from September 2018 to August 2024
  } else if (platformName === 'linkedin') {
    return [2019, 8, 2024, 8]; // Start from August 2019 to August 2024
  }
  // Default case (you can modify it based on your needs)
  return [2018, 1, 2024, 12]; // Example fallback
};

// Generate a list of year/months based on the platform name
const yearMonthList = generateYearlyMonthList(...getYearMonthParameters(platformName));

// console.log(yearMonthList);
  const lastYear = Math.max(...Object.keys(yearMonthList).map(Number)); // Get the last year
  const [selectedYear, setSelectedYear] = useState<number | null>(lastYear);

  const stat = {
    women_13_17: women_13_17 || 0,
    women_13_17_percent: women_13_17_percent || 0,
    men_13_17: men_13_17 || 0,
    men_13_17_percent: men_13_17_percent || 0,
    age_13_17_total_percent: age_13_17_total_percent || 0,

    women_18_24: women_18_24 || 0,
    women_18_24_percent: women_18_24_percent || 0,
    men_18_24: men_18_24 || 0,
    men_18_24_percent: men_18_24_percent || 0,
    age_18_24_total_percent: age_18_24_total_percent || 0,

    women_25_34: women_25_34 || 0,
    women_25_34_percent: women_25_34_percent || 0,
    men_25_34: men_25_34 || 0,
    men_25_34_percent: men_25_34_percent || 0,
    age_25_34_total_percent: age_25_34_total_percent || 0,

    women_35_44: women_35_44 || 0,
    women_35_44_percent: women_35_44_percent || 0,
    men_35_44: men_35_44 || 0,
    men_35_44_percent: men_35_44_percent || 0,
    age_35_44_total_percent: age_35_44_total_percent || 0,

    women_45_54: women_45_54 || 0,
    women_45_54_percent: women_45_54_percent || 0,
    men_45_54: men_45_54 || 0,
    men_45_54_percent: men_45_54_percent || 0,
    age_45_54_total_percent: age_45_54_total_percent || 0,

    women_55_64: women_55_64 || 0,
    women_55_64_percent: women_55_64_percent || 0,
    men_55_64: men_55_64 || 0,
    men_55_64_percent: men_55_64_percent || 0,
    age_55_64_total_percent: age_55_64_total_percent || 0,

    women_65_plus: women_65_plus || 0,
    women_65_plus_percent: women_65_plus_percent || 0,
    men_65_plus: men_65_plus || 0,
    men_65_plus_percent: men_65_plus_percent || 0,

    formattedMonth: formattedMonthString || "",
    womenTotalPercentage: womenTotalPercentage || 0, // Already added
    menTotalPercentage: menTotalPercentage || 0, // Already added
  };

  const linkedInStats = {
    population_18_24: population_18_24 || 0, // Define population for age group 18-24
    population_25_34: population_25_34 || 0, // Define population for age group 25-34
    population_35_54: population_35_54 || 0, // Define population for age group 35-54
    population_55_plus: population_55_plus || 0, // Define population for age group 55+
    percentage_18_24: percentage_18_24 || 0, // Already defined
    percentage_25_34: percentage_25_34 || 0, // Already defined
    percentage_35_54: percentage_35_54 || 0, // Already defined
    percentage_55_plus: percentage_55_plus || 0, // Already defined
    total:
      (population_18_24 || 0) +
      (population_25_34 || 0) +
      (population_35_54 || 0) +
      (population_55_plus || 0), // Calculate total population
  };

  const [searchTerm, setSearchTerm] = useState("");

  const filteredMonths = Object.keys(yearMonthList)
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10)) // Sort years in descending order
    .flatMap((yearStr) => {
      const year = parseInt(yearStr, 10);

      // Filter months based on the search term
      const months = yearMonthList[year].filter((month) => {
        const monthName = monthNames[parseInt(month.split("/")[1], 10) - 1];
        return (
          monthName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          year.toString().includes(searchTerm)
        );
      });

      // Map months to the desired structure and reverse the order
      return months
        .map((month) => ({
          month,
          year,
        }))
        .reverse(); // Reverse the order of months
    });

  const menData = [
    { ageGroup: "13-17", total: men_13_17, percentage: men_13_17_percent },
    { ageGroup: "18-24", total: men_18_24, percentage: men_18_24_percent },
    { ageGroup: "25-34", total: men_25_34, percentage: men_25_34_percent },
    { ageGroup: "35-44", total: men_35_44, percentage: men_35_44_percent },
    { ageGroup: "45-54", total: men_45_54, percentage: men_45_54_percent },
    { ageGroup: "55-64", total: men_55_64, percentage: men_55_64_percent },
    { ageGroup: "65+", total: men_65_plus, percentage: men_65_plus_percent },
  ];

  const womenData = [
    { ageGroup: "13-17", total: women_13_17, percentage: women_13_17_percent },
    { ageGroup: "18-24", total: women_18_24, percentage: women_18_24_percent },
    { ageGroup: "25-34", total: women_25_34, percentage: women_25_34_percent },
    { ageGroup: "35-44", total: women_35_44, percentage: women_35_44_percent },
    { ageGroup: "45-54", total: women_45_54, percentage: women_45_54_percent },
    { ageGroup: "55-64", total: women_55_64, percentage: women_55_64_percent },
    {
      ageGroup: "65+",
      total: women_65_plus,
      percentage: women_65_plus_percent,
    },
  ];

  const linkedinData = [
    {
      ageGroup: "18-24",
      total: population_18_24,
      percentage: percentage_18_24,
    },
    {
      ageGroup: "25-34",
      total: population_25_34,
      percentage: percentage_25_34,
    },
    {
      ageGroup: "35-54",
      total: population_35_54,
      percentage: percentage_35_54,
    },
    {
      ageGroup: "55+",
      total: population_55_plus,
      percentage: percentage_55_plus,
    },
  ];

  // Function to find the highest total and corresponding age group
  const findHighestTotal = (data: any[]) => {
    return data.reduce((max, item) => (item.total > max.total ? item : max), {
      total: 0,
    });
  };

  const highestMen = findHighestTotal(menData);
  const highestWomen = findHighestTotal(womenData);

  const highestotallinkedin = findHighestTotal(linkedinData);

  const title = `${formattedPlatformName} Users in ${
    formattedCountryName || "Country"
  } - ${formattedMonthName} ${year} statistics`;

  const [faqItems, setFaqItems] = useState([
    {
      id: 1,
      question: `How many ${formattedPlatformName} users in ${
        formattedCountryName || "Country"
      } For ${formattedMonthName} ${year}?`,
      answer: `For ${formattedMonthName} ${year}, there are ${formatNumberWithSpaces(
        total || 0
      )} of ${formattedPlatformName} users in ${
        formattedCountryName || "Country"
      }. Here's a breakdown of user distribution by age group and gender for each category.`,
      isOpen: true,
    },
    {
      id: 2,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 13-17?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_13_17 ?? 0) + (men_13_17 ?? 0) || 0
      )} users, accounting for ${
        (women_13_17_percent ?? 0) + (men_13_17_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_13_17 || 0
      )} are male users (${
        men_13_17_percent ?? 0 > 0 ? men_13_17_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_13_17 || 0)} are female users (${
        women_13_17_percent ?? 0 > 0 ? women_13_17_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },

    {
      id: 3,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 18-24?`,
      answer: ` This group has a total of ${formatNumberWithSpaces(
        (women_18_24 ?? 0) + (men_18_24 ?? 0) || 0
      )} users, accounting for ${
        (women_18_24_percent ?? 0) + (men_18_24_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_18_24 || 0
      )} are male users (${
        men_18_24_percent ?? 0 > 0 ? men_18_24_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_18_24 || 0)} are female users (${
        women_18_24_percent ?? 0 > 0 ? women_18_24_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },
    {
      id: 4,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 25-34?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_25_34 ?? 0) + (men_25_34 ?? 0) || 0
      )} users, accounting for ${
        (women_25_34_percent ?? 0) + (men_25_34_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_25_34 || 0
      )} are male users (${
        men_25_34_percent ?? 0 > 0 ? men_25_34_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_25_34 || 0)} are female users (${
        women_25_34_percent ?? 0 > 0 ? women_25_34_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },

    {
      id: 5,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 35-44?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_25_34 ?? 0) + (men_25_34 ?? 0) || 0
      )} users, accounting for ${
        (women_25_34_percent ?? 0) + (men_25_34_percent ?? 0)
      }% of total users. Among them, {formatNumberWithSpaces(men_25_34 || 0)} are male users (${
        men_25_34_percent ?? 0 > 0 ? men_25_34_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_25_34 || 0)} are female users (${
        women_25_34_percent ?? 0 > 0 ? women_25_34_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },
    {
      id: 6,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 45-54`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_45_54 ?? 0) + (men_45_54 ?? 0) || 0
      )} users, accounting for ${
        (women_45_54_percent ?? 0) + (men_45_54_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_45_54 || 0
      )} are male users (${
        men_45_54_percent ?? 0 > 0 ? men_45_54_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_45_54 || 0)} are female users (${
        women_45_54_percent ?? 0 > 0 ? women_45_54_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },
    {
      id: 7,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 55-64?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_55_64 ?? 0) + (men_55_64 ?? 0) || 0
      )} users, accounting for ${
        (women_55_64_percent ?? 0) + (men_55_64_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_55_64 || 0
      )} are male users (${
        men_55_64_percent ?? 0 > 0 ? men_55_64_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_55_64 || 0)} are female users (${
        women_55_64_percent ?? 0 > 0 ? women_55_64_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },
    {
      id: 8,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 65+?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        (women_65_plus ?? 0) + (men_65_plus ?? 0) || 0
      )} users, accounting for ${
        (women_65_plus_percent ?? 0) + (men_65_plus_percent ?? 0)
      }% of total users. Among them, ${formatNumberWithSpaces(
        men_65_plus || 0
      )} are male users (${
        men_65_plus_percent ?? 0 > 0 ? men_65_plus_percent + "%" : "~0.01%"
      }), and ${formatNumberWithSpaces(women_65_plus || 0)} are female users (${
        women_65_plus_percent ?? 0 > 0 ? women_65_plus_percent + "%" : "~0.01%"
      }).`,
      isOpen: true,
    },
  ]);

  const faqItemsLinkedin = [
    {
      id: 1,
      question: `How many ${formattedPlatformName} users in ${
        formattedCountryName || "Country"
      } For ${formattedMonthName} ${year}?`,
      answer: `For ${formattedMonthName} ${year}, there are ${formatNumberWithSpaces(
        total || 0
      )} ${formattedPlatformName} users in ${
        formattedCountryName || "Country"
      }. Here is a breakdown of user distribution by age group.`,
      isOpen: true,
    },
    {
      id: 2,
      question: `How many ${formattedPlatformName} Users Stats Of Age group 18-24?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        population_18_24 || 0
      )} users, which accounts for ${
        percentage_18_24 ?? 0 > 0 ? percentage_18_24 + "%" : "~0.01%"
      } of the total users data.`,
      isOpen: true,
    },

    {
      id: 3,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 25-34?`,
      answer: ` This group has a total of ${formatNumberWithSpaces(
        population_25_34 || 0
      )} users, which accounts for ${
        percentage_25_34 ?? 0 > 0 ? percentage_25_34 + "%" : "~0.01%"
      } of the total users data.`,
      isOpen: true,
    },
    {
      id: 4,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 35-54?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        population_35_54 || 0
      )} users, which accounts for ${
        percentage_35_54 ?? 0 > 0 ? percentage_35_54 + "%" : "~0.01%"
      } of the total users data.`,
      isOpen: true,
    },

    {
      id: 5,
      question: `How many ${formattedPlatformName} Users Stats of Age Group 55+?`,
      answer: `This group has a total of ${formatNumberWithSpaces(
        population_55_plus || 0
      )} users, which accounts for ${
        percentage_55_plus ?? 0 > 0 ? percentage_55_plus + "%" : "~0.01%"
      } of the total users data.`,
      isOpen: true,
    },
  ];

  const faqSchema = FAQSchema(faqItems);

  const faqSchemaLinkedin = FAQSchema(faqItemsLinkedin);

  const chartRef = useRef<HTMLDivElement>(null);

  // Function to handle the download of the chart as a PNG image
  const handleDownloadImage = async () => {
    if (chartRef.current === null) {
      return;
    }

    try {
      // Convert the chart to a PNG image
      const dataUrl = await toPng(chartRef.current);

      // Create a temporary link to download the image
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${platformName}-users-in-${formattedCountryName}-by-age.png`; // File name for the image
      document.body.appendChild(link); // Append the link to the document
      link.click(); // Programmatically click the link to download the image
      document.body.removeChild(link); // Remove the link after download
    } catch (error) {
      console.error("Error converting chart to image", error);
    }
  };



const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbItems.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url ? { "@id": item.url } : undefined,
  })),
};


  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <title>{title || "Default Title"}</title>

        {/* <BreadcrumbSchema items={breadcrumbItems} /> */}
         {/* Breadcrumb Schema */}
         <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />

        {platformName === "facebook" ||
        platformName === "instagram" ||
        platformName === "messenger" ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        ) : platformName === "linkedin" ? (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqSchemaLinkedin),
            }}
          />
        ) : null}

        <meta
          property="og:title"
          content={`${formattedPlatformName} Users in ${
            formattedCountryName || "Country"
          } - ${formattedMonthName} ${year}`}
        />
        <meta
          name="twitter:title"
          content={`${formattedPlatformName} Users in ${
            formattedCountryName || "Country"
          } - ${formattedMonthName} ${year}`}
        />

        <meta
          name="description"
          content={`Get ${formattedPlatformName} users stats in ${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          } for free. The ${formattedPlatformName} demographic data is for ${formattedMonthName} ${year}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />
        <meta
          property="og:description"
          content={`Get ${formattedPlatformName} users stats in ${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          } for free. The ${formattedPlatformName} demographic data is for ${formattedMonthName} ${year}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />
        <meta
          name="twitter:description"
          content={`Get ${formattedPlatformName} users stats in ${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          } for free. The ${formattedPlatformName} demographic data is for ${formattedMonthName} ${year}. It covers top platforms such as LinkedIn, Facebook, Instagram, and more.`}
        />

        {platformName === "facebook" ||
        platformName === "instagram" ||
        platformName === "messenger" ? (
          <meta
            name="keywords"
            content={`Male ${formattedPlatformName} users in ${formattedCountryName} by age, Female ${formattedPlatformName} users in ${formattedCountryName} by age, ${formattedPlatformName} users in ${formattedCountryName} by age, ${formattedPlatformName} Users Age and Gender Distribution Graph in ${formattedCountryName} ${formattedMonthName} ${year}`}
          />
        ) : platformName === "linkedin" ? (
          <meta
            name="keywords"
            content={`${formattedPlatformName} users in ${formattedCountryName} ${formattedMonthName} ${year}, ${formattedPlatformName} users in ${formattedCountryName} by age, ${formattedPlatformName} Users Age Distribution Graph in ${formattedCountryName}`}
          />
        ) : null}

        <link
          rel="canonical"
          href={`https://www.bizdataset.com/social-stats/${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          }/${platformName}-users-in-${formattedMonthName.toLowerCase()}-${year}`}
        />
        <meta
          property="og:url"
          content={`https://www.bizdataset.com/social-stats/${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          }/${platformName}-users-in-${formattedMonthName.toLowerCase()}-${year}`}
        />

        <meta
          property="og:image"
          // content="https://bizdataset-report.s3.eu-north-1.amazonaws.com/asset/favicon-512x512.png"
          content={`https://social-stats.s3.amazonaws.com/${platformName}-users-in-${formattedMonthName.toLowerCase()}-${year}-${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          }-BizDataset.jpeg`}
        />
        <meta
          name="twitter:image"
          content={`https://social-stats.s3.amazonaws.com/${platformName}-users-in-${formattedMonthName.toLowerCase()}-${year}-${
            formattedCountryName?.toLowerCase().replace(/\s+/g, "-") ||
            "country"
          }-BizDataset.jpeg`}        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@bizdataset" />

        <meta name="robots" content="index, follow" />
        <link rel="dns-prefetch" href={`${process.env.SOCIAL_STATS_API_URL}`} />
      </Head>{" "}
      <section className="relative w-full ">
        {/* Pseudo-element for background image with reduced opacity */}
        <div className="absolute inset-0 mt-14 sm:w-auto md:w-auto lg:w-auto flex items-center justify-center sm:ml-4 md:ml-12 lg:ml-24 background-image-custom"></div>

        <div className=" lg:mt-30 mx-2 md:mx-4 lg:mx-0">
          <div className="mb-3 py-9 w-full flex mt-10 items-center justify-center  flex-col relative z-10">
            <Breadcrumbs location={location} />
            <div className="flex items-center uppercase justify-center tracking-wide text-[20px] text-[var(--title-color)] font-semibold  mb-4 md:mb-2">
              {formattedMonthName} {year}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center  mb-4 tracking-wide  text-[var(--title-color)]">
              <span className="gradient-new-text">{formattedPlatformName}</span>{" "}
              users in {formattedCountryName || "Country"}
            </h1>
            <div className="text-[var(--text-color)] max-w-[750px] text-[16px] text-center lg:text-[18px] leading-relaxed mb-4">
              <p>
                Access fresh and reliable {formattedPlatformName} demographics
                data in {formattedCountryName || "Country"} by age and gender
                for {formattedMonthName} {year}.
              </p>
            </div>
            <SocialCounter
              totalRecords={Number(totalRecords)}
              messages={messages}
              showRecords={true}
              showPhoneNumbers={false}
              showEmails={false}
              showFacebook={false}
              icons={selectedIcon ? [selectedIcon] : []} // Wrap the selected icon in an array
            />
          </div>
        </div>
      </section>
      <div className="max-w-[1000px] my-10 md:my-16 mx-auto p-4">
        {/* <div>
          {platformName === "facebook" ||
          platformName === "instagram" ||
          platformName === "messenger" ? (
            // <h2 className="text-2xl font-bold mb-4">
            //   {formattedPlatformName} Users{" "}
            //   <span className="gradient-new-text">
            //     Age and Gender Distribution Graph
            //   </span>{" "}
            //   in {formattedMonthName} {year}
            // </h2>
            <div></div>
          ) : platformName === "linkedin" ? (
            // <h2 className="text-2xl font-bold mb-4">
            //   {formattedPlatformName} Users{" "}
            //   <span className="gradient-new-text">Age Distribution Graph</span>{" "}
            //   in {formattedMonthName} {year}
            // </h2>
            <div> </div>
          ) : (
            <p>No data available for this platform.</p> // Optional: message for unsupported platforms
          )}
        </div> */}

        {/* Chart Component */}
        <div>
          {platformName === "facebook" ||
          platformName === "instagram" ||
          platformName === "messenger" ? (
            // <AgeGenderDistributionChart stats={stat} />
            <div className="">
              {/* Chart Container */}
              <div ref={chartRef} className="bg-white p-4">
              <h2 className="text-2xl font-bold mb-2 text-center">
                 {formattedPlatformName} Users{" "}
                 <span className="gradient-new-text">
                    Age and Gender Distribution Graph
                  </span>
                  </h2>
                {/* Render the AgeGenderDistributionChart component */}
                <h3 className="text-lg mb-8 text-center">
                {formattedCountryName} - {formattedMonthName} {year}
                </h3>
                <div className="relative">
                  {/* Watermark Logo */}
                  <div className="absolute inset-0 flex items-top justify-end mt-8 opacity-20 z-0">
                    <div className="text-[var(--title-color)] text-2xl">
                      <span className="gradient-new-text ">Biz</span>Dataset
                    </div>
                  </div>

                  {/* AgeGenderDistributionChart */}
                  <div className="relative z-10">
                    <AgeGenderDistributionChart stats={stat} />
                  </div>
                </div>

                <div className="text-center mb-4  text-gray-400 mt-4 text-xs">
                  Source: bizdataset.com
                </div>

                <div className="flex item-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#000000"
                    version="1.1"
                    id="Capa_1"
                    width="30px"
                    height="30px"
                    viewBox="0 0 418.683 418.683"
                  >
                    <g>
                      <g>
                        <path d="M310.332,82.426c22.73,0,41.214-18.486,41.214-41.213S333.062,0,310.332,0c-22.724,0-41.213,18.486-41.213,41.213    S287.608,82.426,310.332,82.426z" />
                        <path d="M108.346,82.426c22.727,0,41.213-18.486,41.213-41.213S131.073,0,108.346,0C85.618,0,67.138,18.486,67.138,41.213    S85.624,82.426,108.346,82.426z" />
                        <path d="M206.646,210.849l-31.21-108.68c-1.777-10.003-10.353-17.226-20.518-17.226H61.786c-10.171,0-18.746,7.223-20.525,17.226    l-31.213,108.68c-0.301,1.039-0.275,2.099,0.026,3.06c-0.157,1.002-0.236,1.952-0.236,2.908c0,11.522,9.373,20.897,20.903,20.897    c10.074,0,18.701-7.201,20.428-16.688l13.476-50.035L42.357,278.996c-0.978,2.474-1.471,5.061-1.471,7.701    c0,11.522,9.375,20.898,20.905,20.898h2.273v90.195c0,11.528,9.375,20.893,20.9,20.893c11.522,0,20.898-9.375,20.898-20.893    v-90.195h4.995v90.195c0,11.528,9.373,20.893,20.898,20.893c11.525,0,20.901-9.375,20.901-20.893v-90.195h2.278    c11.525,0,20.896-9.376,20.896-20.898c0-2.661-0.494-5.245-1.473-7.712l-22.276-107.999l13.368,49.61    c1.824,9.917,10.457,17.118,20.53,17.118c11.522,0,20.893-9.375,20.893-20.897c0-0.95-0.082-1.916-0.236-2.914    C206.922,212.942,206.948,211.888,206.646,210.849z" />
                        <path d="M408.607,213.903c0.252-0.808,0.31-1.68,0.141-2.561l-20.514-108.832c-1.648-10.184-10.305-17.567-20.583-17.567H253.01    c-10.283,0-18.936,7.391-20.552,17.431l-20.545,108.968c-0.165,0.881-0.111,1.753,0.139,2.561    c-0.159,0.998-0.234,1.953-0.234,2.914c0,11.522,9.375,20.897,20.903,20.897c10.073,0,18.698-7.201,20.539-17.238l12.946-80.925    l-0.167,258.24c0,11.528,9.37,20.893,20.897,20.893s20.902-9.375,20.902-20.893V269.967h4.986v127.823    c0,11.528,9.377,20.893,20.91,20.893c11.521,0,20.893-9.375,20.893-20.897l-0.169-258.235l12.967,81.045    c1.82,9.917,10.452,17.118,20.525,17.118c11.522,0,20.897-9.375,20.897-20.897C408.844,215.856,408.769,214.89,408.607,213.903z" />
                      </g>
                    </g>
                  </svg>
                  <span className="text-3xl font-bold gradient-new-text">
                    {formatNumberWithSpaces(total || 0)}
                  </span>{" "}
                </div>
                {/* Displaying the total users */}

                {/* {platformName !== "linkedin" && ( */}
                <div className="text-center p-2">
                  <div className="flex justify-center space-x-8">
                    <div>
                      <div className="flex">
                        <span className="text-pink-600 font-extrabold px-1">
                          &#9792;
                        </span>{" "}
                        {/* Female Symbol */}
                        {(womenTotalPercentage ?? 0).toFixed(2)}
                        %
                        <br />
                      </div>
                    </div>
                    <div>
                      <div className="flex">
                        <span className="text-blue-600 font-extrabold px-1">
                          &#9794;
                        </span>{" "}
                        {/* Male Symbol */}
                        {(menTotalPercentage ?? 0).toFixed(2)}
                        %
                        <br />
                      </div>
                    </div>
                  </div>
                </div>
                {/* )} */}
              </div>

              {/* Button to Download Image as PNG */}
              <button
                onClick={handleDownloadImage}
                className=" px-4 mx-auto  flex item-center justify-center py-2 border border-[#0070f0] text-[#0070f0] graadient-new-text  rounded-md  transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#0070f0"
                  width="20px"
                  height="20px"
                  viewBox="0 0 24 24"
                >
                  <path d="M22.71,6.29a1,1,0,0,0-1.42,0L20,7.59V2a1,1,0,0,0-2,0V7.59l-1.29-1.3a1,1,0,0,0-1.42,1.42l3,3a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l3-3A1,1,0,0,0,22.71,6.29ZM19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h8a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21Z" />
                </svg>
                .PNG
              </button>
            </div>
          ) : platformName === "linkedin" ? (
            <>
              {/* <h2 className="text-2xl font-bold mb-4 text-center">
                {formattedPlatformName} Users{" "}
                <span className="gradient-new-text ">
                  Age Distribution Graph
                </span>{" "}
                in {formattedMonthName} {year}
              </h2> */}
              <h2 className="text-2xl font-bold mb-2 text-center">
                 {formattedPlatformName} Users{" "}
                 <span className="gradient-new-text">
                    Age and Gender Distribution Graph
                  </span>
                  </h2>
                {/* Render the AgeGenderDistributionChart component */}
                <h3 className="text-lg mb-8 text-center">
                {formattedCountryName} - {formattedMonthName} {year}
                </h3>

              <div ref={chartRef} className="bg-white p-4">
                <div className="relative">
                  {/* Watermark Logo */}
                  <div className="absolute inset-0 flex items-top justify-end mt-8 opacity-20 z-0">
                    <div className="text-[var(--title-color)] text-2xl">
                      <span className="gradient-new-text ">Biz</span>Dataset
                    </div>
                  </div>

                  {/* AgeGenderDistributionChart */}
                  <div className="relative z-10">
                    <LinkedInUserDistributionChart stats={linkedInStats} />
                  </div>
                </div>

                <div className="text-center mb-2  text-gray-400 mt-4 text-xs">
                  Source: bizdataset.com
                </div>

                <span className="text-3xl font-bold gradient-new-text flex item-center justify-center">
                  {formatNumberWithSpaces(total || 0)}
                </span>
              </div>
              <button
                onClick={handleDownloadImage}
                className=" px-4 mx-auto  flex item-center justify-center py-2 border border-[#0070f0] text-[#0070f0] graadient-new-text  rounded-md  transition-all"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="#0070f0"
                  width="20px"
                  height="20px"
                  viewBox="0 0 24 24"
                >
                  <path d="M22.71,6.29a1,1,0,0,0-1.42,0L20,7.59V2a1,1,0,0,0-2,0V7.59l-1.29-1.3a1,1,0,0,0-1.42,1.42l3,3a1,1,0,0,0,.33.21.94.94,0,0,0,.76,0,1,1,0,0,0,.33-.21l3-3A1,1,0,0,0,22.71,6.29ZM19,13a1,1,0,0,0-1,1v.38L16.52,12.9a2.79,2.79,0,0,0-3.93,0l-.7.7L9.41,11.12a2.85,2.85,0,0,0-3.93,0L4,12.6V7A1,1,0,0,1,5,6h8a1,1,0,0,0,0-2H5A3,3,0,0,0,2,7V19a3,3,0,0,0,3,3H17a3,3,0,0,0,3-3V14A1,1,0,0,0,19,13ZM5,20a1,1,0,0,1-1-1V15.43l2.9-2.9a.79.79,0,0,1,1.09,0l3.17,3.17,0,0L15.46,20Zm13-1a.89.89,0,0,1-.18.53L13.31,15l.7-.7a.77.77,0,0,1,1.1,0L18,17.21Z" />
                </svg>
                .PNG
              </button>
            </>
          ) : (
            <p>No data available for this platform.</p> // Optional: message for unsupported platforms
          )}
        </div>

        {/* Total Users Section */}
        <div className=" mt-4  flex-col  item-center justify-center rounded-lg shadow-md">
          {/* <div className="flex item-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="#000000"
              version="1.1"
              id="Capa_1"
              width="30px"
              height="30px"
              viewBox="0 0 418.683 418.683"
            >
              <g>
                <g>
                  <path d="M310.332,82.426c22.73,0,41.214-18.486,41.214-41.213S333.062,0,310.332,0c-22.724,0-41.213,18.486-41.213,41.213    S287.608,82.426,310.332,82.426z" />
                  <path d="M108.346,82.426c22.727,0,41.213-18.486,41.213-41.213S131.073,0,108.346,0C85.618,0,67.138,18.486,67.138,41.213    S85.624,82.426,108.346,82.426z" />
                  <path d="M206.646,210.849l-31.21-108.68c-1.777-10.003-10.353-17.226-20.518-17.226H61.786c-10.171,0-18.746,7.223-20.525,17.226    l-31.213,108.68c-0.301,1.039-0.275,2.099,0.026,3.06c-0.157,1.002-0.236,1.952-0.236,2.908c0,11.522,9.373,20.897,20.903,20.897    c10.074,0,18.701-7.201,20.428-16.688l13.476-50.035L42.357,278.996c-0.978,2.474-1.471,5.061-1.471,7.701    c0,11.522,9.375,20.898,20.905,20.898h2.273v90.195c0,11.528,9.375,20.893,20.9,20.893c11.522,0,20.898-9.375,20.898-20.893    v-90.195h4.995v90.195c0,11.528,9.373,20.893,20.898,20.893c11.525,0,20.901-9.375,20.901-20.893v-90.195h2.278    c11.525,0,20.896-9.376,20.896-20.898c0-2.661-0.494-5.245-1.473-7.712l-22.276-107.999l13.368,49.61    c1.824,9.917,10.457,17.118,20.53,17.118c11.522,0,20.893-9.375,20.893-20.897c0-0.95-0.082-1.916-0.236-2.914    C206.922,212.942,206.948,211.888,206.646,210.849z" />
                  <path d="M408.607,213.903c0.252-0.808,0.31-1.68,0.141-2.561l-20.514-108.832c-1.648-10.184-10.305-17.567-20.583-17.567H253.01    c-10.283,0-18.936,7.391-20.552,17.431l-20.545,108.968c-0.165,0.881-0.111,1.753,0.139,2.561    c-0.159,0.998-0.234,1.953-0.234,2.914c0,11.522,9.375,20.897,20.903,20.897c10.073,0,18.698-7.201,20.539-17.238l12.946-80.925    l-0.167,258.24c0,11.528,9.37,20.893,20.897,20.893s20.902-9.375,20.902-20.893V269.967h4.986v127.823    c0,11.528,9.377,20.893,20.91,20.893c11.521,0,20.893-9.375,20.893-20.897l-0.169-258.235l12.967,81.045    c1.82,9.917,10.452,17.118,20.525,17.118c11.522,0,20.897-9.375,20.897-20.897C408.844,215.856,408.769,214.89,408.607,213.903z" />
                </g>
              </g>
            </svg>
            <span className="text-3xl font-bold gradient-new-text">
              {formatNumberWithSpaces(total || 0)}
            </span>{" "}
          </div>

          {platformName !== "linkedin" && (
            <div className="text-center p-4">
              <div className="flex justify-center space-x-8">
                <div>
                  <div className="flex">
                    <span className="text-pink-600 font-extrabold px-1">
                      &#9792;
                    </span>{" "}
                    {(womenTotalPercentage ?? 0).toFixed(2)}
                    %
                    <br />
                  </div>
                </div>
                <div>
                  <div className="flex">
                    <span className="text-blue-600 font-extrabold px-1">
                      &#9794;
                    </span>{" "}
                    {(menTotalPercentage ?? 0).toFixed(2)}
                    %
                    <br />
                  </div>
                </div>
              </div>
            </div>
          )} */}

          <div className="p-6 max-w-screen-lg mt-4 mx-auto bg-gradient-to-r from-[#0070f0] to-[#67abf9] rounded-lg shadow-lg text-white">
            {/* <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-center  mb-4 tracking-wide">
              {formattedPlatformName} users in{" "}
              {formattedCountryName || "Country"} by {formattedMonthName} {year}
            </h1> */}
            <div className=" md:text-xl leading-relaxed text-center">
              <div>
                {platformName === "facebook" ||
                platformName === "instagram" ||
                platformName === "messenger" ? (
                  <p>
                    Among all age groups, the {highestMen.ageGroup} age group
                    has the highest number of users with{" "}
                    {formatNumberWithSpaces(
                      (highestMen.total ?? 0) + (highestWomen.total ?? 0) || 0
                    )}{" "}
                    users. {highestMen.ageGroup} age group also has the most
                    male users at{" "}
                    {formatNumberWithSpaces(highestMen.total || 0)}, while the{" "}
                    {highestWomen.ageGroup} age group has the most female users,
                    totaling {formatNumberWithSpaces(highestWomen.total || 0)}.
                  </p>
                ) : platformName === "linkedin" ? (
                  <p>
                    Among all age groups, the {highestotallinkedin.ageGroup} age
                    group has the highest number of users with{" "}
                    {formatNumberWithSpaces(highestotallinkedin.total || 0)}{" "}
                    users.
                  </p>
                ) : (
                  <p>No data available for this platform.</p> // Optional: message for unsupported platforms
                )}
              </div>{" "}
            </div>
          </div>
        </div>

        <div>
          {platformName === "facebook" ||
          platformName === "instagram" ||
          platformName === "messenger" ? (
            <section className="max-w-screen-lg mx-auto  bg-white mt-6  grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border p-4  shadow-md rounded-lg">
                <h2 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} users in{" "}
                  {formattedCountryName || "Country"} For{" "}
                  <span className="gradient-new-text">
                    {formattedMonthName} {year}
                  </span>
                </h2>
                <p className="mb-2">
                  For {formattedMonthName} {year}, there are{" "}
                  {formatNumberWithSpaces(total || 0)} of{" "}
                  {formattedPlatformName} users in{" "}
                  {formattedCountryName || "Country"}. Here&apos;s a breakdown
                  of user distribution by age group and gender for each
                  category.
                </p>
              </div>

              {/* Age group 13-17 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 13-17</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_13_17 ?? 0) + (men_13_17 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_13_17_percent ?? 0) + (men_13_17_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_13_17 || 0)} are male users (
                  {men_13_17_percent ?? 0 > 0
                    ? men_13_17_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_13_17 || 0)} are female
                  users (
                  {women_13_17_percent ?? 0 > 0
                    ? women_13_17_percent + "%"
                    : "~0.01%"}
                  ) .
                </p>
              </div>

              {/* Age group 18-24 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 18-24</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_18_24 ?? 0) + (men_18_24 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_18_24_percent ?? 0) + (men_18_24_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_18_24 || 0)} are male users (
                  {men_18_24_percent ?? 0 > 0
                    ? men_18_24_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_18_24 || 0)} are female
                  users (
                  {women_18_24_percent ?? 0 > 0
                    ? women_18_24_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* Age group 25-34 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 25-34</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_25_34 ?? 0) + (men_25_34 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_25_34_percent ?? 0) + (men_25_34_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_25_34 || 0)} are male users (
                  {men_25_34_percent ?? 0 > 0
                    ? men_25_34_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_25_34 || 0)} are female
                  users (
                  {women_25_34_percent ?? 0 > 0
                    ? women_25_34_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* Age group 35-44 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 35-44</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_35_44 ?? 0) + (men_35_44 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_35_44_percent ?? 0) + (men_35_44_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_35_44 || 0)} are male users (
                  {men_35_44_percent ?? 0 > 0
                    ? men_35_44_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_35_44 || 0)} are female
                  users (
                  {women_35_44_percent ?? 0 > 0
                    ? women_35_44_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* Age group 45-54 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 45-54</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_45_54 ?? 0) + (men_45_54 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_45_54_percent ?? 0) + (men_45_54_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_45_54 || 0)} are male users (
                  {men_45_54_percent ?? 0 > 0
                    ? men_45_54_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_45_54 || 0)} are female
                  users (
                  {women_45_54_percent ?? 0 > 0
                    ? women_45_54_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* Age group 55-64 */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 55-64</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_55_64 ?? 0) + (men_55_64 ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_55_64_percent ?? 0) + (men_55_64_percent ?? 0)}% of
                  total users. Among them,{" "}
                  {formatNumberWithSpaces(men_55_64 || 0)} are male users (
                  {men_55_64_percent ?? 0 > 0
                    ? men_55_64_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_55_64 || 0)} are female
                  users (
                  {women_55_64_percent ?? 0 > 0
                    ? women_55_64_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* Age group 65+ */}
              <div className="border p-4  shadow-md rounded-lg">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  {formattedPlatformName} Users Stats of{" "}
                  <span className="gradient-new-text">Age Group 65+</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(
                    (women_65_plus ?? 0) + (men_65_plus ?? 0) || 0
                  )}{" "}
                  users, accounting for{" "}
                  {(women_65_plus_percent ?? 0) + (men_65_plus_percent ?? 0)}%{" "}
                  of total users. Among them,{" "}
                  {formatNumberWithSpaces(men_65_plus || 0)} are male users (
                  {men_65_plus_percent ?? 0 > 0
                    ? men_65_plus_percent + "%"
                    : "~0.01%"}
                  ), and {formatNumberWithSpaces(women_65_plus || 0)} are female
                  users (
                  {women_65_plus_percent ?? 0 > 0
                    ? women_65_plus_percent + "%"
                    : "~0.01%"}
                  ).
                </p>
              </div>

              {/* <div className="border p-4 mb-4 shadow-md rounded-lg">
                <p className="mb-2">
                  Among all age groups, the {highestMen.ageGroup} age group has
                  the highest number of users with{" "}
                  {(highestMen.total ?? 0) + (highestWomen.total ?? 0)} users.{" "}
                  {highestMen.ageGroup} age group also has the most male users
                  at {highestMen.total}, while the {highestWomen.total} age
                  group has the most female users, totaling{" "}
                  {highestWomen.ageGroup}.
                </p>
              </div> */}
            </section>
          ) : platformName === "linkedin" ? (
            <section className="max-w-screen-lg mx-auto grid grid-cols-1 md:grid-cols-2 bg-white mt-6 gap-4">
              <div className="border p-4 mb-4 shadow-md rounded-lg flex flex-col h-36">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  LinkedIn Users in {formattedCountryName || "Country"} For{" "}
                  <span className="gradient-new-text">
                    {formattedMonthName} {year}
                  </span>
                </h3>
                <p className="mb-2">
                  For {formattedMonthName} {year}, there are{" "}
                  {formatNumberWithSpaces(total || 0)} LinkedIn users in{" "}
                  {formattedCountryName || "Country"}. Here is a breakdown of
                  user distribution by age group.
                </p>
                {/* LinkedIn specific stats can go here if needed */}
              </div>

              <div className="border p-4 shadow-md rounded-lg flex flex-col h-36">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  LinkedIn Users Stats Of{" "}
                  <span className="gradient-new-text">Age group 18-24</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(population_18_24 || 0)} users, which
                  accounts for{" "}
                  {percentage_18_24 ?? 0 > 0
                    ? percentage_18_24 + "%"
                    : "~0.01%"}{" "}
                  of the total users&apos; data.
                </p>
                {/* LinkedIn specific stats can go here if needed */}
              </div>

              <div className="border p-4 shadow-md rounded-lg flex flex-col h-36">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  LinkedIn Users Stats Of{" "}
                  <span className="gradient-new-text">Age group 25-34</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(population_25_34 || 0)} users, which
                  accounts for{" "}
                  {percentage_25_34 ?? 0 > 0
                    ? percentage_25_34 + "%"
                    : "~0.01%"}{" "}
                  of the total users&apos; data.
                </p>
                {/* LinkedIn specific stats can go here if needed */}
              </div>

              <div className="border p-4 shadow-md rounded-lg flex flex-col h-36">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  LinkedIn Users Stats Of{" "}
                  <span className="gradient-new-text">Age group 35-54</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(population_35_54 || 0)} users, which
                  accounts for{" "}
                  {percentage_35_54 ?? 0 > 0
                    ? percentage_35_54 + "%"
                    : "~0.01%"}{" "}
                  of the total users&apos; data.
                </p>
                {/* LinkedIn specific stats can go here if needed */}
              </div>

              <div className="border p-4 shadow-md rounded-lg flex flex-col h-36">
                <h3 className="text-lg font-semibold mt-2 mb-2">
                  LinkedIn Users Stats Of{" "}
                  <span className="gradient-new-text">Age group 55+</span>
                </h3>
                <p className="mb-2">
                  This group has a total of{" "}
                  {formatNumberWithSpaces(population_55_plus || 0)} users, which
                  accounts for{" "}
                  {percentage_55_plus ?? 0 > 0
                    ? percentage_55_plus + "%"
                    : "~0.01%"}{" "}
                  of the total users&apos; data.
                </p>
                {/* LinkedIn specific stats can go here if needed */}
              </div>
            </section>
          ) : (
            <p>No data available for this platform.</p> // Optional: message for unsupported platforms
          )}
        </div>
      </div>
      <div className="flex flex-col items-center mb-4 z-10 mt-16 p-4 ">
        <div className="max-w-screen-lg p-4 w-full rounded-lg ">
          <div className="flex flex-col justify-center p-4">
            <div className="tracking-wide text-[16px] uppercase text-[var(--text-color)] font-semibold mb-2">
              <span className="gradient-new-text">Social Media Stats</span>
            </div>
            <h2 className="text-2xl lg:text-4xl font-semibold mb-2 tracking-wide text-[var(--title-color)]">
              Month-Wise <span className="gradient-new-text">Datasets</span>
            </h2>
            <div className="text-[var(--text-color)] text-[16px] lg:text-[16px] leading-relaxed">
              <p>
                Browse comprehensive data options, from national trends to local
                insights, tailored to meet your specific business needs.
              </p>
            </div>
          </div>
          {/* <table className="w-full h-[300px]">
            <thead>
              <tr>
                <th className="px-4 text-left w-[120px]">Year</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2">
                  <div className="flex flex-col border-t-2 border-slate-300 max-h-[290px] overflow-y-auto">
                    {Object.keys(yearMonthList)
                      .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
                      .map((yearStr) => {
                        const year = parseInt(yearStr, 10);

                        return (
                          <button
                            key={year}
                            onClick={() => setSelectedYear(year)}
                            className={`w-full text-left py-2 ${
                              selectedYear === year
                                ? "underline underline-offset-2 text-[#0070f0]"
                                : "hover:underline hover:underline-offset-2 hover:text-[#0070f0]"
                            }`}
                          >
                            {year}
                          </button>
                        );
                      })}
                  </div>
                </td>

                <td className="px-4 py-2 align-top">
                  {selectedYear && formattedCountryName && platformName && (
                    <div className="grid grid-cols-2 gap-2">
                      {yearMonthList[selectedYear]
                        .sort(
                          (a, b) =>
                            parseInt(b.split("/")[1], 10) -
                            parseInt(a.split("/")[1], 10)
                        )
                        .map((month, index) => (
                          <Link
                            href={`/social-stats/${formattedCountryName
                              .toLowerCase()
                              .replace(
                                /\s+/g,
                                "-"
                              )}/${platformName}-users-in-${monthNames[
                              parseInt(month.split("/")[1], 10) - 1
                            ].toLowerCase()}-${selectedYear}`}
                            key={index}
                          >
                            <div className="block py-2 text-left rounded-lg hover:underline hover:underline-offset-2">
                              {
                                monthNames[
                                  parseInt(month.split("/")[1], 10) - 1
                                ]
                              }
                            </div>
                          </Link>
                        ))}
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table> */}
          <div>
            {/* Search Bar */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by Month or Year..."
                className="border border-gray-300 rounded-lg p-2 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <table className="w-full  h-[300px]">
              <tbody>
                <tr>
                  <td className=" py-2 align-top">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Render filtered months */}
                      {filteredMonths.map(({ month, year }) => (
                        <Link
                          href={`/social-stats/${formattedCountryName
                            .toLowerCase()
                            .replace(
                              /\s+/g,
                              "-"
                            )}/${platformName}-users-in-${monthNames[
                            parseInt(month.split("/")[1], 10) - 1
                          ].toLowerCase()}-${year}`}
                          key={`${month}-${year}`}
                        >
                          <div className="border flex items-center  border-gray-300 rounded-lg shadow-md p-4 hover:bg-gray-100 transition duration-300 ease-in-out">
                            <div className="text-md flex-grow">
                              <div className="flex gap-1">
                                <div className="text-md">
                                  {
                                    monthNames[
                                      parseInt(month.split("/")[1], 10) - 1
                                    ]
                                  }
                                </div>
                                <div className="text-md">{year}</div>
                              </div>
                            </div>
                            <svg
                              width="20px"
                              height="20px"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M4 12H20M20 12L14 6M20 12L14 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default Stats;
